import { createContext, useContext, useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import { filterResults, calculateStats, getUniqueValues, extractTextForLLM, parseResultCenterCodes, CENTER_DIRECTORY } from '../utils/dataProcessor';
import { DEFAULT_PROMPTS } from '../utils/llmService';
import { calculateTrafficLights } from '../utils/trafficLight';
import { loadProgramDescription, loadAllIADescriptions } from '../utils/dataLoader';
import { loadReport, hydrateFromDynamo } from '../utils/reportService';

const AppContext = createContext(null);

const initialState = {
  // Data
  resultsData: [],
  tocData: {},
  isLoading: false,
  error: null,

  // Filters
  filters: {
    source: 'all',
    program: 'all',
    resultScope: 'all',
    year: 'all',
    level: 'all',
    type: 'all',
    impactArea: 'all',
    minScore: 0,
    country: 'all',
    region: 'all',
    fundingType: [],
    tocAlignment: 'all',
    geographicFocus: 'all',
    status: ['Approved', 'Quality Assessed'],
    reportingPhase: ['IPSR 2025', 'Reporting 2025'],
  },

  // Filter options (derived from data)
  filterOptions: {
    programs: [],
    years: [],
    levels: [],
    types: [],
    countries: [],
    regions: [],
    statuses: [],
    reportingPhases: [],
    leadCenters: [],
    centerInvolvement: [],
  },

  // Filtered results cache
  filteredResults: [],
  stats: null,

  // Program info for report
  programInfo: {
    name: '',
    director: '',
    dates: '2025-2030',
    centers: '',
    impactAreas: '',
    geographicFocus: '',
    w3Projects: '',
    budget: '',
    aboutProgram: '',
  },

  // Report sections content
  sections: {
    executiveSummary: '',
    progressNarrative: '',
    areaOfWorkProgress: [],
    partnershipOverview: '',
    portfolioLinkagesOverview: '',
    adaptiveEvidence: '',
    adaptiveResources: '',
    adaptiveBilaterals: '',
    adaptiveRisk: '',
    adaptiveScaling: '',
    adaptivePrioritization: '',
    adaptiveRiskTable: [
      { risk: '', mitigatingAction: '' },
      { risk: '', mitigatingAction: '' },
      { risk: '', mitigatingAction: '' },
      { risk: '', mitigatingAction: '' },
      { risk: '', mitigatingAction: '' },
    ],
    keyResultsStories: [
      {
        title: '',
        standfirst: '',
        outcomeStatement: '',
        mainText: '',
        quote: '',
        quoteAttribution: '',
        impactAreas: [],
        contributingCenters: '',
        contributingPartners: '',
        regions: '',
        countries: '',
      },
    ],
    customBlocks: [],
    // Portfolio Narrative sections
    portfolioOverview: '',
    impactAreaGender: '',
    impactAreaClimate: '',
    impactAreaNutrition: '',
    impactAreaBiodiversity: '',
    impactAreaPoverty: '',
    regionalAfrica: '',
    regionalAmericas: '',
    regionalAsia: '',
    portfolioPartnerships: '',
    // Portfolio Performance Management (manual subsections)
    perfProjectedBenefits: '',
    perfInnovationManagement: '',
    perfAdaptiveManagement: '',
    perfMraTracking: '',
    portfolioCustomBlocks: [],
  },

  // Custom prompts per section
  customPrompts: { ...DEFAULT_PROMPTS },

  // Custom synthesis prompts per section (used when auto-chunking)
  customSynthesisPrompts: {},

  // LLM settings
  llmSettings: {
    provider: 'awsBatch',
    anthropicApiKey: '',
    openaiApiKey: '',
    model: 'claude-opus-4-6',
    useRealLLM: true,
  },

  // Evidence URL maps per section (populated during LLM generation)
  sectionUrlMaps: {},

  // Program description context (loaded from markdown files)
  programDescription: null,

  // Impact Area description context documents keyed by IA key
  iaDescriptions: {},

  // UI state
  activeSection: 'overview',
  isExporting: false,
  reportType: 'program', // 'program' or 'accelerator'
  reportMode: 'technical', // 'technical' or 'portfolio' — top-level mode switcher

  // Section tab state — tracks active figure tab per section for export
  sectionTabs: {
    overview: 'overview',
    progress: 'map',
    progressAow: 'framework',
    partnerships: 'partnerFlow',
    // Portfolio Narrative tabs
    portfolioOverview: 'programBreakdown',
    impactArea: 'gender',
    regionalFocus: 'africa',
    portfolioPartnerships: 'partnerTypes',
  },

  // Export request — { format: 'docx'|'pdf', preview?: boolean } or null
  exportRequest: null,

  // Traffic light ratings
  trafficLights: {
    computed: [],
    overrides: {},
    year: '2025',
    unmatchedMeta: null,
    tocWarning: null, // Set when TOC data is missing for selected program
  },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_RESULTS_DATA': {
      // Accept pre-computed filterOptions from the pipeline, or compute on-the-fly
      const filterOptions = action.filterOptions || getUniqueValues(action.payload);

      // Ensure centerCodes are populated on every result and centerInvolvement
      // is in filterOptions (pipeline pre-computed options may not include it)
      if (!filterOptions.centerInvolvement || filterOptions.centerInvolvement.length === 0) {
        const codes = new Set();
        action.payload.forEach(r => {
          if (!r.centerCodes) r.centerCodes = parseResultCenterCodes(r);
          r.centerCodes.forEach(c => codes.add(c));
        });
        filterOptions.centerInvolvement = [...codes].sort().map(code => ({
          value: code,
          label: CENTER_DIRECTORY.get(code) ? `${code} - ${CENTER_DIRECTORY.get(code)}` : code,
        }));
      }

      const initialFiltered = filterResults(action.payload, state.filters);
      return {
        ...state,
        resultsData: action.payload,
        filterOptions,
        filteredResults: initialFiltered,
        stats: calculateStats(initialFiltered, state.filters),
        isLoading: false,
      };
    }

    case 'SET_TOC_DATA':
      return {
        ...state,
        tocData: { ...state.tocData, [action.payload.programCode]: action.payload.data },
      };

    case 'SET_FILTERS': {
      const newFilters = { ...state.filters, ...action.payload };
      const programChanged = 'program' in action.payload && action.payload.program !== state.filters.program;
      // Reset resultScope when switching programs
      if (programChanged && !('resultScope' in action.payload)) {
        newFilters.resultScope = 'all';
      }
      const filtered = filterResults(state.resultsData, newFilters);
      return {
        ...state,
        filters: newFilters,
        filteredResults: filtered,
        stats: calculateStats(filtered, newFilters),
        ...(programChanged ? { programInfo: { ...initialState.programInfo } } : {}),
      };
    }

    case 'RESET_FILTERS': {
      const resetFiltered = filterResults(state.resultsData, initialState.filters);
      return {
        ...state,
        filters: initialState.filters,
        filteredResults: resetFiltered,
        stats: calculateStats(resetFiltered, initialState.filters),
      };
    }

    case 'SET_PROGRAM_INFO':
      return {
        ...state,
        programInfo: { ...state.programInfo, ...action.payload },
      };

    case 'RESET_PROGRAM_INFO':
      return {
        ...state,
        programInfo: { ...initialState.programInfo },
      };

    case 'SET_SECTION_CONTENT':
      return {
        ...state,
        sections: { ...state.sections, [action.payload.section]: action.payload.content },
      };

    case 'SET_SECTION_URL_MAP':
      return {
        ...state,
        sectionUrlMaps: { ...state.sectionUrlMaps, [action.payload.section]: action.payload.urlMap },
      };

    case 'SET_CUSTOM_PROMPT':
      return {
        ...state,
        customPrompts: { ...state.customPrompts, [action.payload.section]: action.payload.prompt },
      };

    case 'RESET_PROMPT':
      return {
        ...state,
        customPrompts: { ...state.customPrompts, [action.payload]: DEFAULT_PROMPTS[action.payload] },
      };

    case 'SET_CUSTOM_SYNTHESIS_PROMPT':
      return {
        ...state,
        customSynthesisPrompts: { ...state.customSynthesisPrompts, [action.payload.section]: action.payload.prompt },
      };

    case 'RESET_SYNTHESIS_PROMPT':
      return {
        ...state,
        customSynthesisPrompts: { ...state.customSynthesisPrompts, [action.payload]: undefined },
      };

    case 'SET_LLM_SETTINGS':
      return {
        ...state,
        llmSettings: { ...state.llmSettings, ...action.payload },
      };

    case 'SET_ACTIVE_SECTION':
      return { ...state, activeSection: action.payload };

    case 'SET_EXPORTING':
      return { ...state, isExporting: action.payload };

    case 'SET_SECTION_TAB':
      return {
        ...state,
        sectionTabs: { ...state.sectionTabs, [action.payload.section]: action.payload.tab },
      };

    case 'SET_EXPORT_REQUEST':
      return { ...state, exportRequest: action.payload, isExporting: !!action.payload };

    case 'SET_REPORT_TYPE':
      return { ...state, reportType: action.payload };

    case 'SET_REPORT_MODE':
      return {
        ...state,
        reportMode: action.payload,
        // When switching modes, set the default active section for that mode
        activeSection: action.payload === 'portfolio' ? 'portfolio-overview' : 'overview',
      };

    case 'SET_TRAFFIC_LIGHTS': {
      const prev = state.trafficLights.computed;
      const next = action.payload;
      const nextWarning = action.tocWarning ?? null;
      // Bail out if structurally identical — no new object = no re-render
      if (state.trafficLights.tocWarning === nextWarning &&
          prev.length === next.length && prev.every((aow, i) =>
        aow.name === next[i].name &&
        aow.rating === next[i].rating &&
        aow.progress === next[i].progress &&
        aow.resultCount === next[i].resultCount &&
        (aow.hlos?.length || 0) === (next[i].hlos?.length || 0)
      )) {
        return state;
      }
      return { ...state, trafficLights: { ...state.trafficLights, computed: next, tocWarning: nextWarning, unmatchedMeta: action.unmatchedMeta || null } };
    }

    case 'SET_TRAFFIC_LIGHT_OVERRIDE':
      return {
        ...state,
        trafficLights: {
          ...state.trafficLights,
          overrides: {
            ...state.trafficLights.overrides,
            [action.payload.aowName]: {
              rating: action.payload.rating,
              justification: action.payload.justification,
            },
          },
        },
      };

    case 'SET_PROGRAM_DESCRIPTION':
      return { ...state, programDescription: action.payload };

    case 'SET_IA_DESCRIPTIONS':
      return { ...state, iaDescriptions: action.payload };

    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Actions
  const setLoading = useCallback(loading => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback(error => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const setResultsData = useCallback((data, precomputedFilterOptions) => {
    dispatch({ type: 'SET_RESULTS_DATA', payload: data, filterOptions: precomputedFilterOptions || null });
  }, []);

  const setTocData = useCallback((programCode, data) => {
    dispatch({ type: 'SET_TOC_DATA', payload: { programCode, data } });
  }, []);

  const setFilters = useCallback(filters => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' });
  }, []);

  const setProgramInfo = useCallback(info => {
    dispatch({ type: 'SET_PROGRAM_INFO', payload: info });
  }, []);

  const setSectionContent = useCallback((section, content) => {
    dispatch({ type: 'SET_SECTION_CONTENT', payload: { section, content } });
  }, []);

  const setSectionUrlMap = useCallback((section, urlMap) => {
    dispatch({ type: 'SET_SECTION_URL_MAP', payload: { section, urlMap } });
  }, []);

  const setCustomPrompt = useCallback((section, prompt) => {
    dispatch({ type: 'SET_CUSTOM_PROMPT', payload: { section, prompt } });
  }, []);

  const resetPrompt = useCallback(section => {
    dispatch({ type: 'RESET_PROMPT', payload: section });
  }, []);

  const setCustomSynthesisPrompt = useCallback((section, prompt) => {
    dispatch({ type: 'SET_CUSTOM_SYNTHESIS_PROMPT', payload: { section, prompt } });
  }, []);

  const resetSynthesisPrompt = useCallback(section => {
    dispatch({ type: 'RESET_SYNTHESIS_PROMPT', payload: section });
  }, []);

  const setLlmSettings = useCallback(settings => {
    dispatch({ type: 'SET_LLM_SETTINGS', payload: settings });
  }, []);

  const setActiveSection = useCallback(section => {
    dispatch({ type: 'SET_ACTIVE_SECTION', payload: section });
  }, []);

  const setExporting = useCallback(exporting => {
    dispatch({ type: 'SET_EXPORTING', payload: exporting });
  }, []);

  const setSectionTab = useCallback((section, tab) => {
    dispatch({ type: 'SET_SECTION_TAB', payload: { section, tab } });
  }, []);

  const setExportRequest = useCallback(request => {
    dispatch({ type: 'SET_EXPORT_REQUEST', payload: request });
  }, []);

  const setReportType = useCallback(type => {
    dispatch({ type: 'SET_REPORT_TYPE', payload: type });
  }, []);

  const setReportMode = useCallback(mode => {
    dispatch({ type: 'SET_REPORT_MODE', payload: mode });
  }, []);

  const setTrafficLights = useCallback(computed => {
    dispatch({ type: 'SET_TRAFFIC_LIGHTS', payload: computed });
  }, []);

  const setTrafficLightOverride = useCallback((aowName, rating, justification) => {
    dispatch({ type: 'SET_TRAFFIC_LIGHT_OVERRIDE', payload: { aowName, rating, justification } });
  }, []);

  // Derived data — returns { items, urlMap }
  const getTextForLLM = useCallback((enabledColumns) => {
    return extractTextForLLM(state.filteredResults, enabledColumns);
  }, [state.filteredResults]);

  // All results with data filters applied (but NOT program-filtered) — used for
  // cross-program contribution matching in traffic light calculation while still
  // respecting Status / Reporting Phase / Funding Type filters.
  const dataFilteredAll = useMemo(() => {
    return filterResults(state.resultsData, { ...state.filters, program: 'all' });
  }, [state.resultsData, state.filters]);

  // Compute traffic lights globally so they update regardless of which section is active
  const lastTrafficLightInputs = useRef(null);
  useEffect(() => {
    if (state.filteredResults.length > 0 && Object.keys(state.tocData).length > 0) {
      const tocKeys = Object.keys(state.tocData).sort().join(',');
      const inputKey = `${JSON.stringify(state.filters)}|${state.trafficLights.year}|${tocKeys}`;
      if (lastTrafficLightInputs.current === inputKey) return;
      lastTrafficLightInputs.current = inputKey;

      const selectedProgram = state.filters.program !== 'all' ? state.filters.program : Object.keys(state.tocData)[0];
      const programToc = state.tocData[selectedProgram];

      if (!programToc) {
        // No TOC data for this program — show empty traffic lights with a warning.
        // NEVER fall back to a different program's TOC (this caused silent data mismatches).
        console.warn(`No TOC data available for ${selectedProgram}. Traffic lights cannot be computed.`);
        dispatch({
          type: 'SET_TRAFFIC_LIGHTS',
          payload: [],
          tocWarning: `No Theory of Change data available for ${selectedProgram}. Traffic light ratings cannot be computed. Please ensure the TOC file includes data for this program, or regenerate processed.json with the pipeline.`,
        });
        return;
      }

      const { aows: computed, unmatchedMeta } = calculateTrafficLights(programToc, state.filteredResults, state.trafficLights.year, dataFilteredAll, state.filters.resultScope || 'all');
      dispatch({ type: 'SET_TRAFFIC_LIGHTS', payload: computed, tocWarning: null, unmatchedMeta });
    }
  }, [state.filteredResults, state.tocData, state.filters, state.trafficLights.year, dataFilteredAll]);

  // Load program description when selected program changes
  // (programInfo reset is handled atomically inside the SET_FILTERS reducer)
  useEffect(() => {
    const code = state.filters.program;
    if (!code || code === 'all') {
      dispatch({ type: 'SET_PROGRAM_DESCRIPTION', payload: null });
      return;
    }
    let cancelled = false;
    loadProgramDescription(code).then(desc => {
      if (!cancelled) {
        dispatch({ type: 'SET_PROGRAM_DESCRIPTION', payload: desc });
      }
    });
    return () => { cancelled = true; };
  }, [state.filters.program]);

  // Load Impact Area descriptions once on mount
  useEffect(() => {
    loadAllIADescriptions().then(map => {
      dispatch({ type: 'SET_IA_DESCRIPTIONS', payload: map });
    });
  }, []);

  // Load saved report data from DynamoDB when program changes
  const lastLoadedProgram = useRef(null);
  useEffect(() => {
    const code = state.filters.program;
    if (!code || code === 'all') {
      lastLoadedProgram.current = null;
      return;
    }
    if (lastLoadedProgram.current === code) return;
    lastLoadedProgram.current = code;

    let cancelled = false;
    loadReport(code).then(items => {
      if (cancelled || !items.length) return;
      console.log(`[reportService] Loaded ${items.length} saved sections for ${code}`);
      hydrateFromDynamo(items, { setSectionContent, setProgramInfo });
    });
    return () => { cancelled = true; };
  }, [state.filters.program, setSectionContent, setProgramInfo]);

  // Load saved portfolio sections from DynamoDB when switching to portfolio mode.
  // Portfolio sections use a fixed key 'PORTFOLIO' instead of a program code.
  const lastLoadedPortfolio = useRef(false);
  useEffect(() => {
    if (state.reportMode !== 'portfolio') {
      lastLoadedPortfolio.current = false;
      return;
    }
    if (lastLoadedPortfolio.current) return;
    lastLoadedPortfolio.current = true;

    let cancelled = false;
    loadReport('PORTFOLIO').then(items => {
      if (cancelled || !items.length) return;
      console.log(`[reportService] Loaded ${items.length} saved portfolio sections`);
      hydrateFromDynamo(items, { setSectionContent, setProgramInfo });
    });
    return () => { cancelled = true; };
  }, [state.reportMode, setSectionContent, setProgramInfo]);

  // Data subsets — accessible variables for future narrative generation
  // These are stored/computed so that any section or narrative tool can access
  // the exact data subset used, ensuring transparency and reproducibility.
  const dataSubsets = useMemo(() => ({
    all: state.filteredResults,
    w1w2: state.filteredResults.filter(r => r.fundingType !== 'W3Bilateral'),
    w3Bilateral: state.filteredResults.filter(r => r.fundingType === 'W3Bilateral'),
    byType: Object.fromEntries(
      [...new Set(state.filteredResults.map(r => r.type).filter(Boolean))].map(type => [
        type,
        state.filteredResults.filter(r => r.type === type),
      ])
    ),
    withPartners: state.filteredResults.filter(r => r.partners || r.partnersKP),
    withToc: state.filteredResults.filter(r => r.tocMappingType === 'aligned-planned' || r.tocMappingType === 'aligned-unplanned'),
    withEvidence: state.filteredResults.filter(r => r.evidence && r.evidence.length > 0),
  }), [state.filteredResults]);

  const value = {
    ...state,
    dataSubsets,
    setLoading,
    setError,
    setResultsData,
    setTocData,
    setFilters,
    resetFilters,
    setProgramInfo,
    setSectionContent,
    setSectionUrlMap,
    setCustomPrompt,
    resetPrompt,
    setCustomSynthesisPrompt,
    resetSynthesisPrompt,
    setLlmSettings,
    setActiveSection,
    setExporting,
    setSectionTab,
    setExportRequest,
    setReportType,
    setReportMode,
    setTrafficLights,
    setTrafficLightOverride,
    getTextForLLM,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
